import { PRACTICE_SLOT_NUMBERS } from '../domain/raidPracticeSlots.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { bossDisplayName } from '../../../shared/domain/bossPresentation.js';
import { deleteRaidPractice, loadRaidPractice, saveRaidPractice } from '../api/raidPracticeApi.js';
import { mergePracticeWithActual } from '../domain/raidPracticeMatcher.js';
import { buildPracticeBossSummaries } from '../domain/raidPracticeView.js';
import { useRaidPracticeRecommendationCatalog } from './useRaidPracticeRecommendationCatalog.js';

const EMPTY = {
  unionId: '', unionName: '', viewer: { memberOpenid: '', nickname: '', role: '' },
  seasons: [], selectedSeason: null,
};
const SELECTED_BOSS_STORAGE_KEY = 'dororaid:selected-practice-boss';

function initialBossNo() {
  if (typeof window === 'undefined') return 1;
  const stored = Number(window.localStorage.getItem(SELECTED_BOSS_STORAGE_KEY));
  return Number.isInteger(stored) && stored >= 1 && stored <= 5 ? stored : 1;
}
function rememberBossNo(bossNo) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(SELECTED_BOSS_STORAGE_KEY, String(bossNo));
}
function firstFreeSlot(records, bossNo, reserved = []) {
  const used = new Set(records.filter((record) => Number(record.bossNo) === Number(bossNo)).map((record) => Number(record.slotNo)));
  reserved.filter((item) => Number(item.bossNo) === Number(bossNo)).forEach((item) => used.add(Number(item.slotNo)));
  return PRACTICE_SLOT_NUMBERS.find((slotNo) => !used.has(slotNo)) || null;
}
function practiceBossName(bosses, bossNo) {
  const boss = (Array.isArray(bosses) ? bosses : []).find((item) => Number(item.bossNo) === Number(bossNo));
  return bossDisplayName(boss || { bossNo });
}

export function useRaidPractice({ enabled = true } = {}) {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(enabled);
  const [working, setWorking] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [notice, setNotice] = useState({ type: '', message: '' });
  const [selectedBossNo, setSelectedBossNo] = useState(initialBossNo);
  const runRef = useRef(0);
  const selectedSeasonNoRef = useRef(null);
  const selectedBossNoRef = useRef(selectedBossNo);
  const requestRef = useRef({ key: '', promise: null });
  const blockingRequestRef = useRef(null);
  const {
    catalog,
    loading: catalogLoading,
    errorMessage: catalogErrorMessage,
  } = useRaidPracticeRecommendationCatalog({ enabled });

  const refresh = useCallback(async (seasonNo = null, { silent = false } = {}) => {
    if (!enabled) return null;
    const requestedSeasonNo = seasonNo == null ? null : Number(seasonNo);
    const normalizedSeasonNo = Number.isFinite(requestedSeasonNo) ? requestedSeasonNo : null;
    const requestKey = normalizedSeasonNo == null ? 'current' : String(normalizedSeasonNo);
    const runId = runRef.current + 1;
    runRef.current = runId;

    let requestPromise = requestRef.current.key === requestKey
      ? requestRef.current.promise
      : null;
    if (!requestPromise) {
      requestPromise = loadRaidPractice(normalizedSeasonNo);
      requestRef.current = { key: requestKey, promise: requestPromise };
    }

    if (!silent) {
      blockingRequestRef.current = requestPromise;
      setLoading(true);
    } else if (blockingRequestRef.current) {
      blockingRequestRef.current = requestPromise;
    }

    try {
      const next = await requestPromise;
      if (runRef.current !== runId) return null;
      selectedSeasonNoRef.current = next?.selectedSeason?.seasonNo || null;
      setData(next);
      setErrorMessage('');
      const bosses = next.selectedSeason?.bosses || [];
      if (bosses.length && !bosses.some((boss) => Number(boss.bossNo) === Number(selectedBossNoRef.current))) {
        const fallbackBossNo = Number(bosses[0].bossNo);
        selectedBossNoRef.current = fallbackBossNo;
        setSelectedBossNo(fallbackBossNo);
        rememberBossNo(fallbackBossNo);
      }
      return next;
    } catch (error) {
      if (runRef.current === runId) setErrorMessage(error instanceof Error ? error.message : '모의전 정보를 불러오지 못했습니다.');
      return null;
    } finally {
      if (requestRef.current.promise === requestPromise) requestRef.current = { key: '', promise: null };
      if (blockingRequestRef.current === requestPromise) {
        blockingRequestRef.current = null;
        setLoading(false);
      }
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      runRef.current += 1;
      setLoading(false);
      setErrorMessage('');
      return undefined;
    }
    refresh();
    const changed = () => refresh(selectedSeasonNoRef.current, { silent: true });
    window.addEventListener('dororaid:raid-overview-changed', changed);
    return () => {
      runRef.current += 1;
      window.removeEventListener('dororaid:raid-overview-changed', changed);
    };
  }, [enabled, refresh]);

  const selectSeason = useCallback((seasonNo) => {
    const normalized = Number(seasonNo);
    if (!Number.isInteger(normalized)) return;
    refresh(normalized);
  }, [refresh]);
  const selectBoss = useCallback((bossNo) => {
    const normalized = Number(bossNo);
    if (!Number.isInteger(normalized)) return;
    selectedBossNoRef.current = normalized;
    setSelectedBossNo(normalized);
    rememberBossNo(normalized);
  }, []);

  const save = useCallback(async (payload) => {
    if (working || !data.selectedSeason) return false;
    setWorking(`save:${payload.slotNo}`);
    setNotice({ type: '', message: '' });
    try {
      await saveRaidPractice({ ...payload, seasonNo: data.selectedSeason.seasonNo, bossNo: selectedBossNo });
      await refresh(data.selectedSeason.seasonNo, { silent: true });
      setNotice({ type: 'success', message: `${practiceBossName(data.selectedSeason.bosses, selectedBossNo)} ${payload.slotNo}번 슬롯을 저장했습니다.` });
      return true;
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '모의전 기록을 저장하지 못했습니다.' });
      return false;
    } finally { setWorking(''); }
  }, [data.selectedSeason, refresh, selectedBossNo, working]);

  const saveThreeHits = useCallback(async (hits) => {
    if (working || !data.selectedSeason || !Array.isArray(hits) || !hits.length) return false;
    const allCodes = hits.flatMap((hit) => (hit.deck || []).map((code) => String(code || '').trim().toLocaleLowerCase('ko-KR')).filter(Boolean));
    if (new Set(allCodes).size !== allCodes.length) {
      setNotice({ type: 'error', message: '3타 전체에서 동일한 니케를 중복 사용할 수 없습니다.' });
      return false;
    }
    const records = data.selectedSeason.practiceRecords || [];
    const reserved = [];
    const payloads = [];
    for (const hit of hits.slice(0, 3)) {
      const slotNo = firstFreeSlot(records, hit.bossNo, reserved);
      if (!slotNo) {
        setNotice({ type: 'error', message: `${practiceBossName(data.selectedSeason.bosses, hit.bossNo)}에 비어 있는 모의전 슬롯이 없습니다.` });
        return false;
      }
      reserved.push({ bossNo: Number(hit.bossNo), slotNo });
      payloads.push({ ...hit, slotNo, seasonNo: data.selectedSeason.seasonNo, bossNo: Number(hit.bossNo), used: false });
    }
    setWorking('save:three-hit');
    setNotice({ type: '', message: '' });
    try {
      await Promise.all(payloads.map((payload) => saveRaidPractice(payload)));
      await refresh(data.selectedSeason.seasonNo, { silent: true });
      setNotice({ type: 'success', message: `${payloads.length}타 모의전 슬롯을 저장했습니다.` });
      return true;
    } catch (error) {
      await refresh(data.selectedSeason.seasonNo, { silent: true });
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '3타 모의전 기록을 저장하지 못했습니다.' });
      return false;
    } finally { setWorking(''); }
  }, [data.selectedSeason, refresh, working]);

  const remove = useCallback(async (recordId, slotNo) => {
    if (working || !recordId || !data.selectedSeason) return false;
    setWorking(`delete:${slotNo}`);
    setNotice({ type: '', message: '' });
    try {
      await deleteRaidPractice(recordId);
      await refresh(data.selectedSeason.seasonNo, { silent: true });
      setNotice({ type: 'success', message: `${practiceBossName(data.selectedSeason.bosses, selectedBossNo)} ${slotNo}번 슬롯을 삭제했습니다.` });
      return true;
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '모의전 기록을 삭제하지 못했습니다.' });
      return false;
    } finally { setWorking(''); }
  }, [data.selectedSeason, refresh, selectedBossNo, working]);

  const [practiceOnly, setPracticeOnly] = useState(false);
  const bossSummaries = useMemo(() => buildPracticeBossSummaries({ bosses: data.selectedSeason?.bosses || [], practiceRecords: data.selectedSeason?.practiceRecords || [], actualRecords: data.selectedSeason?.actualRecords || [] }), [data.selectedSeason?.actualRecords, data.selectedSeason?.bosses, data.selectedSeason?.practiceRecords]);
  const selectedBoss = useMemo(() => bossSummaries.find((boss) => boss.bossNo === selectedBossNo) || bossSummaries[0] || null, [bossSummaries, selectedBossNo]);
  // Once a real attack lands it replaces the practice card it matched. '모의전만 보기' rebuilds the
  // same list without the real records, so the registered 모의전 slots stay readable.
  const mergedSlots = useMemo(() => (catalog ? mergePracticeWithActual({
    manualRecords: data.selectedSeason?.practiceRecords || [],
    actualRecords: practiceOnly ? [] : data.selectedSeason?.actualRecords || [],
    bossNo: selectedBoss?.bossNo || selectedBossNo,
    catalog,
  }) : []), [catalog, data.selectedSeason?.actualRecords, data.selectedSeason?.practiceRecords, practiceOnly, selectedBoss?.bossNo, selectedBossNo]);
  // The toggle only means something once a real attack has actually replaced something on this boss.
  const hasActualRecords = useMemo(() => (data.selectedSeason?.actualRecords || [])
    .some((record) => Number(record.bossNo) === Number(selectedBoss?.bossNo || selectedBossNo)), [data.selectedSeason?.actualRecords, selectedBoss?.bossNo, selectedBossNo]);
  const nextSlotNo = useMemo(() => firstFreeSlot(data.selectedSeason?.practiceRecords || [], selectedBoss?.bossNo || selectedBossNo), [data.selectedSeason?.practiceRecords, selectedBoss?.bossNo, selectedBossNo]);

  return {
    ...data,
    loading: enabled ? loading || catalogLoading : false,
    working,
    errorMessage: enabled ? errorMessage || catalogErrorMessage : '',
    notice,
    bossSummaries,
    selectedBoss,
    selectedBossNo: selectedBoss?.bossNo || selectedBossNo,
    mergedSlots,
    visibleSlots: mergedSlots,
    practiceOnly,
    setPracticeOnly,
    hasActualRecords,
    nextSlotNo,
    refresh,
    selectSeason,
    selectBoss,
    save,
    saveThreeHits,
    remove,
  };
}
